import { useState, useEffect, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Shell } from "@/components/Shell";
import { MyJobsTab } from "@/components/worker/MyJobsTab";
import { CompletedJobsTab } from "@/components/worker/CompletedJobsTab";
import { TimesheetTab } from "@/components/worker/TimesheetTab";
import { MyRosterTab } from "@/components/worker/MyRosterTab";
import { NotLinkedGate } from "@/components/worker/NotLinkedGate";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { hasModule } from "@/lib/modules";
import { Briefcase, Clock, CalendarRange, CheckCircle2 } from "lucide-react";

const TABS = [
  { v: "jobs", label: "My Jobs", icon: Briefcase, C: MyJobsTab, need: "jobs" },
  { v: "completed", label: "Completed", icon: CheckCircle2, C: CompletedJobsTab, need: "jobs" },
  { v: "timesheet", label: "Timesheet", icon: Clock, C: TimesheetTab },
  { v: "roster", label: "My Week", icon: CalendarRange, C: MyRosterTab },
];

// A worker is "linked to a company" once a coordinator adds them to their crew
// (checked server-side via /my/linked), even before any job or shift exists.
export default function WorkerDashboard() {
  const [state, setState] = useState("checking"); // checking | linked | unlinked
  const [rechecking, setRechecking] = useState(false);
  const { company, refresh: refreshCompany } = useCompany();
  const tabs = TABS.filter((t) => hasModule(company, t.need));
  const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const initialTab = ["jobs", "completed", "timesheet", "roster"].includes(params.get("tab")) ? params.get("tab") : "jobs";
  const [tab, setTab] = useState(initialTab);
  const current = tabs.some((t) => t.v === tab) ? tab : tabs[0].v;
  const [focusJobId, setFocusJobId] = useState(params.get("job") || null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.search) {
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const check = useCallback(async (isRecheck = false) => {
    if (isRecheck) setRechecking(true);
    try {
      const res = await api.myLinked();
      setState(res?.linked ? "linked" : "unlinked");
      if (res?.linked && isRecheck) refreshCompany();
    } catch {
      // On an unexpected error, don't lock the worker out — show the dashboard.
      setState("linked");
    } finally {
      if (isRecheck) setRechecking(false);
    }
  }, [refreshCompany]);

  useEffect(() => { check(); }, [check]);

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="worker-checking">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state === "unlinked") {
    return <NotLinkedGate onRecheck={() => check(true)} rechecking={rechecking} />;
  }

  return (
    <Tabs value={current} onValueChange={setTab} className="w-full">
      <Shell
        title="Field Worker"
        tabs={
          <TabsList className="h-auto bg-transparent p-0 gap-1 overflow-x-auto flex justify-start w-full rounded-none">
            {tabs.map((t) => {
              const Icon = t.icon;
              return (
                <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none data-[state=active]:bg-transparent px-4 py-3 text-sm font-medium text-muted-foreground">
                  <Icon className="w-4 h-4 mr-1.5" /> {t.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        }
      >
        {tabs.map((t) => {
          const C = t.C;
          const props = t.v === "jobs" ? { focusJobId, onFocusHandled: () => setFocusJobId(null) } : {};
          return <TabsContent key={t.v} value={t.v} className="mt-0"><C {...props} /></TabsContent>;
        })}
      </Shell>
    </Tabs>
  );
}
